import type { Key, KeyboardEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { EventDataNode } from 'rc-tree/es/interface';
import { selectMultiple } from '../../store/actions/nodes/selectMultiple';
import { togglePane } from '../../store/slices/pane';
import { expandTreeNode } from '../../store/actions/tree/expand';
import { reorderNode } from '../../store/actions/tree/reorderNode';
import {
  selectSearchFocusIdForCatalog,
  selectTreeDataByCatalogId,
} from '../../store/selectors/tree/tree';
import { catalogConfigSelector } from '../../store/selectors/tree/data';
import { selectedTreeNodeKeysByTreeIdSelector } from '../../store/selectors/tree/node';
import { getPathById } from '../../utils/tree/treeNode';
import {
  NODE_TYPE_CATALOG,
  NODE_TYPE_CATEGORY,
  NODE_TYPE_ITEM,
  NODE_TYPE_MULTI_SELECT,
  NODE_TYPE_SUGGESTION,
} from '../../utils/types/nodeTypes';
import { CATALOG_TYPE_ITEMS_CATEGORY_RECOMMENDATIONS } from '../../utils/types/catalogTypes';
import Loading from '../Loading';
import CommonTree from './CommonTree/CommonTree';
import { useTreeShortcutKeys } from './useTreeShortcutKeys';
import { useOnMount } from '../../hooks/useOnMount';
import { fetchTree } from '../../store/actions/tree/fetchTree';
import { selectSuggestionFocusIdForCatalog } from '../../store/slices/tree';
import type { AppDispatch, RootState } from '../../store';
import type { Catalog } from '../../entities/Catalog';
import { selectIsGlobalMarket } from '../../store/slices/intl';
import {
  selectCategoryRecommendationsLoading,
  selectCategoryRecommendationsLoaded,
} from '../../store/slices/item';
import type { OnSelectEvent } from './treeTypes';
import type { NodeBase } from '../../entities/Node';

interface DropInfo {
  node: NodeBase;
  dragNode: NodeBase;
  dropPosition: number;
  dropToGap: boolean;
}

const Tree = ({
  catalog,
  tree,
  pane,
}: {
  catalog: Catalog;
  tree: string;
  pane?: string;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [scrollToKey, setScrollToKey] = useState<number | string | null>(null);
  const isGlobalMarket = useSelector(selectIsGlobalMarket);
  const treeData = useSelector((state: RootState) =>
    selectTreeDataByCatalogId(state, catalog.catalogId),
  );
  const categoryRecommendationsLoading = useSelector(
    selectCategoryRecommendationsLoading,
  );
  const categoryRecommendationsLoaded = useSelector(
    selectCategoryRecommendationsLoaded,
  );
  const isItemRecommendationsCatalog =
    catalog.catalogType === CATALOG_TYPE_ITEMS_CATEGORY_RECOMMENDATIONS;

  const catalogConfig = useSelector((state) =>
    catalogConfigSelector(state, catalog.catalogId),
  );

  // Enable dragging if any ranking is allowed
  const draggable =
    catalogConfig.allowRanking || catalogConfig.allowRankingLevels.length > 0;

  const selectedKeys = useSelector<RootState>((state) =>
    selectedTreeNodeKeysByTreeIdSelector(state, catalog.catalogId),
  ) as string[];

  const searchFocusId = useSelector<RootState>((state) =>
    selectSearchFocusIdForCatalog(state, catalog.catalogId),
  ) as string;
  const suggestionFocusId = useSelector<RootState>((state) =>
    selectSuggestionFocusIdForCatalog(state, catalog.catalogId),
  ) as string;

  const selectTreeNode = useCallback(
    (keys: Key[], event: OnSelectEvent) => {
      if (event.node.type === NODE_TYPE_MULTI_SELECT) {
        return null;
      }

      const nodeItem = { node: { ...event.node }, path: getPathById(event.node.id) };
      const { shiftKey, ctrlKey, metaKey } = event.nativeEvent || {};

      return dispatch(
        selectMultiple(tree, nodeItem, { shiftKey, ctrlKey, metaKey }),
      );
    },
    [dispatch, tree],
  );

  const onDrop = useCallback(
    (info: DropInfo) => {
      const { dragNode, node: dropNode, dropPosition } = info;

      dispatch(
        reorderNode({
          tree,
          catalogId: catalog.catalogId,
          dragNode,
          dropNode,
          dropPosition,
        }),
      );
    },
    [dispatch, tree, catalog.catalogId],
  );

  const onDoubleClick = useCallback(
    (_: unknown, node: { type: string }) => {
      if (
        [
          NODE_TYPE_CATALOG,
          NODE_TYPE_CATEGORY,
          NODE_TYPE_ITEM,
          NODE_TYPE_SUGGESTION,
        ].includes(node.type)
      ) {
        dispatch(togglePane(true));
      }
    },
    [dispatch],
  );
  const onExpand = useCallback(
    (treeNode: {
      node: EventDataNode<NodeBase>;
      expanded: boolean;
      nativeEvent: MouseEvent;
    }) => {
      dispatch(expandTreeNode(tree, treeNode));
    },
    [dispatch, tree],
  );

  const [onKeyDown, onActiveChange] = useTreeShortcutKeys(catalog.catalogId);

  const keyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // @ts-expect-error useTreeShortcutKeys not typed
    onKeyDown(event);
  };

  const onActive = (key: Key) => {
    // @ts-expect-error useTreeShortcutKeys not typed
    onActiveChange(key);
  };

  useOnMount(() => {
    dispatch(
      fetchTree({
        tree,
        catalog,
        isGlobalMarket,
      }),
    );
  });

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setScrollToKey(searchFocusId), [searchFocusId]);
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setScrollToKey(suggestionFocusId), [suggestionFocusId]);

  return treeData.length ? (
    <CommonTree
      autoExpandParent={false}
      onActiveChange={onActive}
      indentTree={catalogConfig.indentTree}
      onDoubleClick={onDoubleClick}
      onExpand={onExpand}
      onKeyDown={keyDown}
      onSelect={selectTreeNode}
      scrollToKey={scrollToKey}
      selectedKeys={selectedKeys}
      treeNodes={treeData}
      draggable={draggable}
      allowRanking={catalogConfig.allowRanking}
      allowRankingLevels={catalogConfig.allowRankingLevels}
      onDrop={onDrop}
      pane={pane}
      catalogId={catalog.catalogId}
    />
  ) : isItemRecommendationsCatalog &&
    categoryRecommendationsLoaded &&
    !categoryRecommendationsLoading ? (
    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
      No uncategorised items
    </div>
  ) : (
    <Loading text="Catalogue is loading, please wait..." />
  );
};

export default Tree;
